import * as React from "react";
import { Icon } from "@hubper/core-ui/elements";
import { Config, ContextType } from "../../context";
import { oneOfKeysChanged, getLabel } from "../../util";

import * as SortIcons from "../SortIcons";
import { CloseWrapper, HeaderCellBase, Inner, Label, Right } from "./styles";

import filterComponents from "../Filters";

function useFocusFilterAfterClick(filterShow: boolean) {
  const shouldFocus = React.useRef<boolean>(false);
  const filterRef = React.useRef<{ focus?: () => void }>(null);

  // auto focus filter components when clicked by hand
  React.useLayoutEffect(() => {
    if (filterShow && shouldFocus.current) {
      shouldFocus.current = false;
      filterRef.current!.focus!();
    }
  }, [filterShow]);

  return {
    filterRef,
    afterLabelClick: () => (shouldFocus.current = true)
  };
}

type HeaderCellProps = {
  width: number;
  config: Config;
  filterShow: boolean;
  filterValue: any;
} & Pick<
  ContextType,
  | "t"
  | "rowColor2"
  | "toggleSort"
  | "sortKey"
  | "sortDirection"
  | "setFilterShow"
>;

function HeaderCell({
  width,
  config,
  rowColor2,
  t,
  toggleSort,
  sortKey,
  sortDirection,
  setFilterShow,
  filterShow
}: HeaderCellProps) {
  const label = getLabel(config.label, config.key, t);
  const isSortActive = sortKey === config.key;
  const key = config.key as string;

  const { filterRef, afterLabelClick } = useFocusFilterAfterClick(filterShow);

  const FilterComponent = filterComponents[config.type];

  return (
    <HeaderCellBase
      data-testid={`header-cell-${label}`}
      key={label as string}
      style={{
        width,
        backgroundColor: rowColor2
      }}
    >
      <Inner>
        {filterShow ? (
          <>
            <FilterComponent config={config} ref={filterRef} />
            <CloseWrapper
              data-testid="header-cell-close-filter"
              onClick={() => setFilterShow(key, false)}
            >
              <Icon name="close" size={16} />
            </CloseWrapper>
          </>
        ) : (
          <Label
            onClick={() => {
              setFilterShow(key, true);
              // set focus on the filter element we've just enabled
              afterLabelClick();
            }}
          >
            {label}
          </Label>
        )}
        <Right
          data-testid="header-cell-sort"
          onClick={() => toggleSort(key)}
          active={isSortActive}
        >
          {isSortActive && sortDirection === "asc" && <SortIcons.Up />}
          {isSortActive && sortDirection === "desc" && <SortIcons.Down />}
          {!isSortActive && <SortIcons.UpDown />}
        </Right>
      </Inner>
    </HeaderCellBase>
  );
}

export default React.memo(
  HeaderCell,
  (prev: HeaderCellProps, next: HeaderCellProps) => {
    const key = prev.config.key as string;

    if (
      oneOfKeysChanged(
        ["config", "rowColor2", "filterShow", "width"],
        prev,
        next
      )
    ) {
      return false;
    }

    const hasLostSorting =
      prev.sortKey && !next.sortKey && prev.sortKey === key;
    const justGotSorting =
      !prev.sortKey && next.sortKey && next.sortKey === key;
    const isCurrentlySortingButWithNewDirection =
      next.sortKey &&
      next.sortKey === key &&
      next.sortDirection !== prev.sortDirection;

    if (
      hasLostSorting ||
      justGotSorting ||
      isCurrentlySortingButWithNewDirection
    ) {
      return false;
    }

    const isCurrentlyFilteringButWithNewValue =
      prev.filterShow &&
      next.filterShow &&
      prev.filterValue !== next.filterValue;

    if (isCurrentlyFilteringButWithNewValue) {
      return false;
    }

    return true;
  }
);
